import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Drop, Bell, Plus, Minus, Clock, GearSix } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AlertasPage() {
  const [config, setConfig] = useState(null);
  const [waterLog, setWaterLog] = useState({ glasses: 0 });
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchConfig = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/alerts/config`); setConfig(data); } catch {} finally { setLoading(false); }
  }, []);
  const fetchWater = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/alerts/water-log`); setWaterLog(data); } catch {}
  }, []);
  useEffect(() => { fetchConfig(); fetchWater(); }, [fetchConfig, fetchWater]);

  const logWater = async (action) => {
    try { const { data } = await axios.post(`${API}/api/alerts/water-log`, { action }); setWaterLog(data); } catch {}
  };
  const saveConfig = async (updates) => {
    try {
      const { data } = await axios.put(`${API}/api/alerts/config`, { ...config, ...updates });
      setConfig(data); toast.success('Configurações guardadas');
    } catch { toast.error('Erro ao guardar'); }
  };

  const glasses = waterLog.glasses || 0;
  const goalLiters = config?.water_goal_liters || 1.5;
  const glassesGoal = Math.ceil(goalLiters / 0.25);
  const waterPct = Math.min(100, Math.round((glasses / glassesGoal) * 100));
  const mealTimes = config?.meal_times || [];

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  return (
    <div className="fade-in" data-testid="alertas-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Lembretes</h2>
          <p className="text-sm text-brand-muted mt-1">Refeições e hidratação</p>
        </div>
        <button onClick={() => setShowSettings(true)} className="p-2 rounded-full hover:bg-brand-bg transition-colors" data-testid="alert-settings-btn">
          <GearSix size={20} className="text-brand-muted" />
        </button>
      </div>

      {/* Water tracker */}
      <div className="bg-white border border-brand-border rounded-3xl p-5 mb-5 shadow-[0_8px_32px_rgba(101,113,97,0.04)]" data-testid="water-tracker">
        <div className="flex items-center gap-2 mb-4">
          <Drop size={20} weight="duotone" className="text-blue-400" />
          <h3 className="font-heading font-medium text-sm text-brand-text">Água de hoje</h3>
        </div>
        <div className="flex items-center justify-center gap-6 mb-4">
          <button onClick={() => logWater('remove')} className="w-10 h-10 rounded-full border border-brand-border flex items-center justify-center text-brand-muted hover:bg-brand-bg transition-colors" data-testid="water-minus"><Minus size={18} /></button>
          <div className="text-center">
            <p className="text-4xl font-heading font-semibold text-brand-text">{glasses}</p>
            <p className="text-xs text-brand-muted">de {glassesGoal} copos</p>
          </div>
          <button onClick={() => logWater('add')} className="w-10 h-10 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-500 hover:bg-blue-100 transition-colors" data-testid="water-plus"><Plus size={18} /></button>
        </div>
        {/* Progress bar */}
        <div className="w-full h-3 bg-brand-border rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${waterPct >= 100 ? 'bg-brand-green' : waterPct >= 50 ? 'bg-blue-400' : 'bg-blue-300'}`} style={{ width: `${waterPct}%` }} />
        </div>
        <p className="text-xs text-brand-muted text-center mt-2">
          {waterPct >= 100 ? 'Objetivo atingido!' : `${(glasses * 0.25).toFixed(1)}L de ${goalLiters}L`}
        </p>
      </div>

      {/* Meal reminders */}
      <div className="bg-white border border-brand-border rounded-3xl p-5 shadow-[0_8px_32px_rgba(101,113,97,0.04)]" data-testid="meal-reminders">
        <div className="flex items-center gap-2 mb-4">
          <Bell size={20} weight="duotone" className="text-brand-gold" />
          <h3 className="font-heading font-medium text-sm text-brand-text">Horários de refeição</h3>
        </div>
        {mealTimes.length === 0 ? (
          <p className="text-xs text-brand-muted text-center py-4">Sem horários configurados.</p>
        ) : (
          <div className="space-y-2">
            {mealTimes.map((time, i) => {
              const now = new Date();
              const [h, m] = time.split(':').map(Number);
              const mealDate = new Date(); mealDate.setHours(h, m, 0);
              const isPast = now > mealDate;
              const isNext = !isPast && (i === 0 || (() => { const prev = mealTimes[i-1]?.split(':').map(Number); if (!prev) return true; const pd = new Date(); pd.setHours(prev[0], prev[1], 0); return now > pd; })());
              return (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-2xl ${isNext ? 'bg-brand-mint/10 border border-brand-mint/20' : isPast ? 'bg-brand-bg/30' : 'bg-white border border-brand-border/50'}`}>
                  <Clock size={16} className={isNext ? 'text-brand-green' : isPast ? 'text-brand-placeholder' : 'text-brand-muted'} />
                  <span className={`text-sm font-medium ${isNext ? 'text-brand-green' : isPast ? 'text-brand-placeholder' : 'text-brand-text'}`}>{time}</span>
                  {isNext && <span className="text-[10px] text-brand-green ml-auto">Próxima</span>}
                  {isPast && <span className="text-[10px] text-brand-placeholder ml-auto">Passada</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Settings dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Configurações</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <Label className="text-sm text-brand-text">Lembretes de refeição</Label>
              <Switch checked={config?.meal_alerts ?? true} onCheckedChange={v => saveConfig({ meal_alerts: v })} data-testid="toggle-meal-alerts" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm text-brand-text">Lembretes de água</Label>
              <Switch checked={config?.water_alerts ?? true} onCheckedChange={v => saveConfig({ water_alerts: v })} data-testid="toggle-water-alerts" />
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Objetivo de água (litros)</Label>
              <Input type="number" step="0.25" value={config?.water_goal_liters || 1.5} onChange={e => setConfig({ ...config, water_goal_liters: parseFloat(e.target.value) || 1.5 })} onBlur={() => saveConfig({ water_goal_liters: config?.water_goal_liters })} className="w-24 rounded-xl border-brand-border" />
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Horários de refeição</Label>
              <div className="space-y-1.5">
                {(config?.meal_times || []).map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input type="time" value={t} onChange={e => { const u = [...(config?.meal_times || [])]; u[i] = e.target.value; setConfig({ ...config, meal_times: u }); }} className="rounded-xl border-brand-border text-sm" />
                    <button onClick={() => { const u = (config?.meal_times || []).filter((_, j) => j !== i); setConfig({ ...config, meal_times: u }); }} className="text-red-400 text-xs">Remover</button>
                  </div>
                ))}
                <button onClick={() => setConfig({ ...config, meal_times: [...(config?.meal_times || []), '12:00'] })} className="text-xs text-brand-green flex items-center gap-1"><Plus size={12} /> Adicionar horário</button>
              </div>
              <button onClick={() => saveConfig({ meal_times: config?.meal_times })} className="w-full bg-brand-green text-white rounded-full py-2.5 font-medium text-sm mt-3">Guardar horários</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
